import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import "./NavigationStyle.css";
import logo from "../../assets/images/kaphy-insurance-agency.png";
import { useAuth } from "../../context/AuthContext";

// Custom smooth scroll function with offset for fixed navigation
const scrollWithOffset = (el) => {
  const yCoordinate = el.getBoundingClientRect().top + window.pageYOffset;
  const yOffset = -80; // Offset for fixed navbar
  window.scrollTo({
    top: yCoordinate + yOffset,
    behavior: "smooth",
  });
};

const Navigation = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const navbarRef = useRef(null);
  const megaMenuRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);

  // Quote mega menu state & refs
  const quoteMenuRef = useRef(null);
  const quoteCloseTimerRef = useRef(null);
  const [isQuoteMenuOpen, setIsQuoteMenuOpen] = useState(false);

  // Locations dropdown state & refs
  const locationsMenuRef = useRef(null);
  const locationsCloseTimerRef = useRef(null);
  const [isLocationsMenuOpen, setIsLocationsMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/api/auth/login");
  };

  // Function to close mobile menu and reset burger icon
  const closeMobileMenu = () => {
    const navbarCollapse = document.querySelector(".navbar-collapse");
    const navbarToggler = document.querySelector(".navbar-toggler");

    if (navbarCollapse && navbarCollapse.classList.contains("show")) {
      navbarCollapse.classList.remove("show");
    }

    if (navbarToggler) {
      navbarToggler.classList.remove("collapsed");
      navbarToggler.setAttribute("aria-expanded", "false");
    }

    // Close any open standard dropdown menus
    const openDropdowns = document.querySelectorAll(
      ".nav-dropdown-menu.nav-mobile-show"
    );
    openDropdowns.forEach((menu) => {
      menu.classList.remove("nav-mobile-show");
    });

    // Close all mega / locations menus
    setIsMegaMenuOpen(false);
    setIsQuoteMenuOpen(false);
    setIsLocationsMenuOpen(false);
  };

  // ── Mega Menu handlers ──────────────────────────────────────────────

  // Desktop: open on hover — also cancels any pending close timer so that
  // re-entering the li or the panel itself always keeps the menu alive.
  const handleMegaMenuMouseEnter = useCallback(() => {
    if (window.innerWidth >= 992) {
      clearTimeout(closeTimerRef.current);
      setIsMegaMenuOpen(true);
    }
  }, []);

  // Desktop: close on mouse-leave — debounced so the cursor can travel
  // across the ~16 px gap that exists between the li's layout box and the
  // absolutely-positioned mega-menu panel without prematurely closing it.
  // If the cursor enters the panel (or re-enters the li) within 180 ms,
  // handleMegaMenuMouseEnter fires and cancels this timer.
  const handleMegaMenuMouseLeave = useCallback(() => {
    if (window.innerWidth >= 992) {
      closeTimerRef.current = setTimeout(() => setIsMegaMenuOpen(false), 180);
    }
  }, []);

  // Mobile: toggle on click (prevent navigation to /our-products)
  const handleMegaMenuToggle = (e) => {
    if (window.innerWidth < 992) {
      e.preventDefault();
      e.stopPropagation();
      setIsQuoteMenuOpen(false); // close sibling mega menu
      setIsMegaMenuOpen((prev) => !prev);
    }
  };

  // Close after navigating to an item inside the mega menu
  const handleMegaMenuLinkClick = () => {
    if (window.innerWidth < 992) {
      setTimeout(closeMobileMenu, 100);
      window.scrollTo(0, 0);
    } else {
      setIsMegaMenuOpen(false);
    }
  };

  // ── Quote Mega Menu handlers ─────────────────────────────────────────────

  const handleQuoteMenuMouseEnter = useCallback(() => {
    if (window.innerWidth >= 992) {
      clearTimeout(quoteCloseTimerRef.current);
      setIsQuoteMenuOpen(true);
    }
  }, []);

  const handleQuoteMenuMouseLeave = useCallback(() => {
    if (window.innerWidth >= 992) {
      quoteCloseTimerRef.current = setTimeout(
        () => setIsQuoteMenuOpen(false),
        180
      );
    }
  }, []);

  const handleQuoteMenuToggle = (e) => {
    if (window.innerWidth < 992) {
      e.preventDefault();
      e.stopPropagation();
      setIsMegaMenuOpen(false); // close sibling mega menu
      setIsQuoteMenuOpen((prev) => !prev);
    }
  };

  const handleQuoteMenuLinkClick = () => {
    if (window.innerWidth < 992) {
      setTimeout(closeMobileMenu, 100);
      window.scrollTo(0, 0);
    } else {
      setIsQuoteMenuOpen(false);
    }
  };

  // ── Locations dropdown handlers ──────────────────────────────────────────

  const handleLocationsMenuMouseEnter = useCallback(() => {
    if (window.innerWidth >= 992) {
      clearTimeout(locationsCloseTimerRef.current);
      setIsLocationsMenuOpen(true);
    }
  }, []);

  const handleLocationsMenuMouseLeave = useCallback(() => {
    if (window.innerWidth >= 992) {
      locationsCloseTimerRef.current = setTimeout(
        () => setIsLocationsMenuOpen(false),
        180
      );
    }
  }, []);

  const handleLocationsMenuToggle = (e) => {
    if (window.innerWidth < 992) {
      e.preventDefault();
      e.stopPropagation();
      setIsMegaMenuOpen(false);
      setIsQuoteMenuOpen(false);
      setIsLocationsMenuOpen((prev) => !prev);
    }
  };

  const handleLocationsMenuLinkClick = () => {
    if (window.innerWidth < 992) {
      setTimeout(closeMobileMenu, 100);
      window.scrollTo(0, 0);
    } else {
      setIsLocationsMenuOpen(false);
    }
  };

  // Close mega menus when clicking outside them (desktop focus / keyboard nav)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (megaMenuRef.current && !megaMenuRef.current.contains(e.target)) {
        setIsMegaMenuOpen(false);
      }
      if (quoteMenuRef.current && !quoteMenuRef.current.contains(e.target)) {
        setIsQuoteMenuOpen(false);
      }
      if (locationsMenuRef.current && !locationsMenuRef.current.contains(e.target)) {
        setIsLocationsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mega menu on ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsMegaMenuOpen(false);
        setIsQuoteMenuOpen(false);
        setIsLocationsMenuOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Clear any pending close timers when the component unmounts
  useEffect(() => {
    return () => {
      clearTimeout(closeTimerRef.current);
      clearTimeout(quoteCloseTimerRef.current);
      clearTimeout(locationsCloseTimerRef.current);
    };
  }, []);

  // ── Standard dropdown & scroll/resize behaviour (unchanged) ────────

  useEffect(() => {
    const initDropdowns = () => {
      // Targets standard dropdowns only; mega menu li has no .nav-dropdown-menu child
      // so querySelector returns null and handlers are safely no-ops for it.
      const dropdowns = document.querySelectorAll(
        ".nav-dropdown, .nav-dropdown-submenu"
      );

      const handleMouseEnter = (dropdown) => {
        const menu = dropdown.querySelector(".nav-dropdown-menu");
        if (menu && window.innerWidth >= 992) {
          menu.style.display = "block";
          setTimeout(() => menu.classList.add("nav-show"), 10);
        }
      };

      const handleMouseLeave = (dropdown) => {
        const menu = dropdown.querySelector(".nav-dropdown-menu");
        if (menu && window.innerWidth >= 992) {
          menu.classList.remove("nav-show");
          setTimeout(() => {
            if (!menu.classList.contains("nav-show")) {
              menu.style.display = "none";
            }
          }, 300);
        }
      };

      dropdowns.forEach((dropdown) => {
        dropdown.addEventListener("mouseenter", () =>
          handleMouseEnter(dropdown)
        );
        dropdown.addEventListener("mouseleave", () =>
          handleMouseLeave(dropdown)
        );
      });

      return () => {
        dropdowns.forEach((dropdown) => {
          dropdown.removeEventListener("mouseenter", () =>
            handleMouseEnter(dropdown)
          );
          dropdown.removeEventListener("mouseleave", () =>
            handleMouseLeave(dropdown)
          );
        });
      };
    };

    const initMobileMenu = () => {
      // Exclude the mega-menu-toggle from the standard dropdown toggle handler
      // so that our React state handler takes full ownership of it.
      const allNavLinks = document.querySelectorAll(
        ".nav-link:not(.nav-dropdown-toggle), .nav-dropdown-item"
      );
      const dropdownToggles = document.querySelectorAll(
        ".nav-dropdown-toggle:not(.mega-menu-toggle)"
      );

      const handleNavLinkClick = () => {
        if (window.innerWidth < 992) {
          setTimeout(() => {
            closeMobileMenu();
          }, 100);
          window.scrollTo(0, 0);
        }
      };

      const handleDropdownToggleClick = (e) => {
        if (window.innerWidth < 992) {
          e.preventDefault();
          e.stopPropagation();

          // Also close mega menus when any other dropdown opens on mobile
          setIsMegaMenuOpen(false);
          setIsQuoteMenuOpen(false);

          const menu = e.currentTarget.nextElementSibling;
          const currentToggle = e.currentTarget;

          if (menu) {
            // Close other open dropdowns
            const allDropdowns =
              document.querySelectorAll(".nav-dropdown-menu");
            allDropdowns.forEach((dropdown) => {
              if (dropdown !== menu) {
                dropdown.classList.remove("nav-mobile-show");
              }
            });

            // Toggle current dropdown
            menu.classList.toggle("nav-mobile-show");

            // Update aria-expanded
            const isExpanded = menu.classList.contains("nav-mobile-show");
            currentToggle.setAttribute("aria-expanded", isExpanded.toString());
          }
        }
      };

      // Add event listeners to all navigation links
      allNavLinks.forEach((link) => {
        link.addEventListener("click", handleNavLinkClick);
      });

      // Add event listeners to dropdown toggles (excluding mega menu toggle)
      dropdownToggles.forEach((toggle) => {
        toggle.addEventListener("click", handleDropdownToggleClick);
      });

      return () => {
        allNavLinks.forEach((link) => {
          link.removeEventListener("click", handleNavLinkClick);
        });
        dropdownToggles.forEach((toggle) => {
          toggle.removeEventListener("click", handleDropdownToggleClick);
        });
      };
    };

    const handleScroll = () => {
      if (navbarRef.current) {
        if (window.scrollY > 50) {
          navbarRef.current.classList.add("nav-scrolled");
        } else {
          navbarRef.current.classList.remove("nav-scrolled");
        }
      }
    };

    // Handle window resize to close mobile menu when switching to desktop
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        closeMobileMenu();
      }
    };

    const cleanupDropdowns = initDropdowns();
    const cleanupMobileMenu = initMobileMenu();
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);
    handleScroll();

    return () => {
      cleanupDropdowns?.();
      cleanupMobileMenu?.();
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [navigate]);

  return (
    <header className="nav-header">
      <nav className="navbar navbar-expand-lg nav-main" ref={navbarRef}>
        <div className="container-fluid px-lg-5">
          <Link className="navbar-brand nav-brand" to="/">
            <img className="nav-logo" src={logo} alt="Kaphy Insurance Agency" />
          </Link>

          <button
            className="navbar-toggler nav-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
            aria-controls="navbarNav"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav nav-menu ms-auto">
              <li className="nav-item">
                <Link className="nav-link nav-text" to="/">
                  Home
                </Link>
              </li>

              {/* ── Our Products · Mega Menu ─────────────────────────── */}
              <li
                className="nav-item nav-dropdown nav-mega-item"
                ref={megaMenuRef}
                onMouseEnter={handleMegaMenuMouseEnter}
                onMouseLeave={handleMegaMenuMouseLeave}
              >
                {/*
                  Classes:
                    nav-dropdown-toggle  → keeps existing chevron-rotation CSS
                    mega-menu-toggle     → excludes it from the DOM-based
                                           initMobileMenu toggle handler
                */}
                <Link
                  className="nav-link nav-dropdown-toggle mega-menu-toggle nav-text"
                  to="/our-products"
                  role="button"
                  aria-haspopup="true"
                  aria-expanded={isMegaMenuOpen}
                  onClick={handleMegaMenuToggle}
                >
                  Our Products
                  <i className="fa-solid fa-chevron-down nav-chevron"></i>
                </Link>

                <div
                  className={`mega-menu-container${
                    isMegaMenuOpen ? " mega-menu-is-open" : ""
                  }`}
                  onMouseEnter={handleMegaMenuMouseEnter}
                  onMouseLeave={handleMegaMenuMouseLeave}
                  role="region"
                  aria-label="Our Products menu"
                >
                  <div className="mega-menu-inner">

                    {/* ── Column 1 · Personal Insurance ── */}
                    <div className="mega-menu-col">
                      <div className="mega-menu-col-header">
                        <span className="mega-menu-col-header-icon">
                          <i className="fa-solid fa-user"></i>
                        </span>
                        <span className="mega-menu-col-title">
                          Personal Insurance
                        </span>
                      </div>

                      <HashLink
                        smooth
                        scroll={scrollWithOffset}
                        className="mega-menu-link"
                        to="/our-products#auto-insurance"
                        onClick={handleMegaMenuLinkClick}
                      >
                        <span className="mega-menu-link-icon-wrap">
                          <i className="fa-solid fa-car"></i>
                        </span>
                        <span className="mega-menu-link-body">
                          <span className="mega-menu-link-title">
                            Auto Insurance
                          </span>
                          <span className="mega-menu-link-desc">
                            Protect your vehicle &amp; stay road-legal with
                            comprehensive coverage
                          </span>
                        </span>
                      </HashLink>

                      <HashLink
                        smooth
                        scroll={scrollWithOffset}
                        className="mega-menu-link"
                        to="/our-products#home-insurance"
                        onClick={handleMegaMenuLinkClick}
                      >
                        <span className="mega-menu-link-icon-wrap">
                          <i className="fa-solid fa-house"></i>
                        </span>
                        <span className="mega-menu-link-body">
                          <span className="mega-menu-link-title">
                            Home Insurance
                          </span>
                          <span className="mega-menu-link-desc">
                            Safeguard your home &amp; belongings from
                            unexpected events
                          </span>
                        </span>
                      </HashLink>

                      <Link
                        className="mega-menu-link"
                        to="/renters-insurance"
                        onClick={handleMegaMenuLinkClick}
                      >
                        <span className="mega-menu-link-icon-wrap">
                          <i className="fa-solid fa-couch"></i>
                        </span>
                        <span className="mega-menu-link-body">
                          <span className="mega-menu-link-title">
                            Renters Insurance
                          </span>
                          <span className="mega-menu-link-desc">
                            Affordable protection for your apartment or rental
                            home
                          </span>
                        </span>
                      </Link>

                      <Link
                        className="mega-menu-link"
                        to="/life-insurance"
                        onClick={handleMegaMenuLinkClick}
                      >
                        <span className="mega-menu-link-icon-wrap">
                          <i className="fa-solid fa-heart"></i>
                        </span>
                        <span className="mega-menu-link-body">
                          <span className="mega-menu-link-title">
                            Life Insurance
                          </span>
                          <span className="mega-menu-link-desc">
                            Protect your family's financial future with life
                            coverage through Progressive
                          </span>
                        </span>
                      </Link>

                      <Link
                        className="mega-menu-link"
                        to="/insurance-carriers"
                        onClick={handleMegaMenuLinkClick}
                      >
                        <span className="mega-menu-link-icon-wrap">
                          <i className="fa-solid fa-building-columns"></i>
                        </span>
                        <span className="mega-menu-link-body">
                          <span className="mega-menu-link-title">
                            Insurance Carriers
                          </span>
                          <span className="mega-menu-link-desc">
                            Explore the carriers available through our
                            independent agency
                          </span>
                        </span>
                      </Link>

                      <Link
                        className="mega-menu-view-all"
                        to="/our-products"
                        onClick={handleMegaMenuLinkClick}
                      >
                        View All Products&nbsp;
                        <i className="fa-solid fa-arrow-right"></i>
                      </Link>
                    </div>

                    {/* ── Column 2 · Commercial Insurance ── */}
                    <div className="mega-menu-col">
                      <div className="mega-menu-col-header">
                        <span className="mega-menu-col-header-icon">
                          <i className="fa-solid fa-briefcase"></i>
                        </span>
                        <span className="mega-menu-col-title">
                          Commercial Insurance
                        </span>
                      </div>

                      <HashLink
                        smooth
                        scroll={scrollWithOffset}
                        className="mega-menu-link"
                        to="/our-products#commercial-insurance"
                        onClick={handleMegaMenuLinkClick}
                      >
                        <span className="mega-menu-link-icon-wrap">
                          <i className="fa-solid fa-building"></i>
                        </span>
                        <span className="mega-menu-link-body">
                          <span className="mega-menu-link-title">
                            Commercial Insurance
                          </span>
                          <span className="mega-menu-link-desc">
                            Tailored coverage solutions for businesses of all
                            sizes
                          </span>
                        </span>
                      </HashLink>

                      <HashLink
                        smooth
                        scroll={scrollWithOffset}
                        className="mega-menu-link"
                        to="/our-products#commercial-general-liability"
                        onClick={handleMegaMenuLinkClick}
                      >
                        <span className="mega-menu-link-icon-wrap">
                          <i className="fa-solid fa-shield-halved"></i>
                        </span>
                        <span className="mega-menu-link-body">
                          <span className="mega-menu-link-title">
                            Errors &amp; Omissions / E&amp;O
                          </span>
                          <span className="mega-menu-link-desc">
                            Shield your business from third-party claims
                            &amp; lawsuits
                          </span>
                        </span>
                      </HashLink>

                      <HashLink
                        smooth
                        scroll={scrollWithOffset}
                        className="mega-menu-link"
                        to="/our-products#commercial-insurance"
                        onClick={handleMegaMenuLinkClick}
                      >
                        <span className="mega-menu-link-icon-wrap">
                          <i className="fa-solid fa-warehouse"></i>
                        </span>
                        <span className="mega-menu-link-body">
                          <span className="mega-menu-link-title">
                            Property Insurance
                          </span>
                          <span className="mega-menu-link-desc">
                            Protect your business assets, equipment &amp;
                            inventory
                          </span>
                        </span>
                      </HashLink>
                    </div>

                    {/* ── Column 3 · CTA Card ── */}
                    <div className="mega-menu-cta-col">
                      <div className="mega-menu-cta-card">
                        <span className="mega-menu-cta-badge">
                          <i className="fa-solid fa-bolt"></i>&nbsp;Free Quote
                        </span>
                        <p className="mega-menu-cta-title">
                          Find the right coverage for you today
                        </p>
                        <p className="mega-menu-cta-text">
                          Our licensed agents will help you get the best rates
                          on the coverage you need.
                        </p>
                        <Link
                          className="mega-menu-cta-btn"
                          to="/get-quote-today"
                          onClick={handleMegaMenuLinkClick}
                        >
                          <i className="fa-solid fa-file-invoice"></i>&nbsp;Get
                          a Free Quote
                        </Link>
                        <Link
                          className="mega-menu-cta-secondary"
                          to="/contact-us"
                          onClick={handleMegaMenuLinkClick}
                        >
                          <i className="fa-regular fa-envelope"></i>&nbsp;Contact
                          an Agent
                        </Link>
                      </div>
                    </div>

                  </div>
                </div>
              </li>
              {/* ── End Our Products · Mega Menu ──────────────────────── */}

              {/* ── Quote · Mega Menu ────────────────────────────── */}
              <li
                className="nav-item nav-dropdown nav-mega-item"
                ref={quoteMenuRef}
                onMouseEnter={handleQuoteMenuMouseEnter}
                onMouseLeave={handleQuoteMenuMouseLeave}
              >
                <Link
                  className="nav-link nav-dropdown-toggle mega-menu-toggle nav-text"
                  to="/get-quote-today"
                  role="button"
                  aria-haspopup="true"
                  aria-expanded={isQuoteMenuOpen}
                  onClick={handleQuoteMenuToggle}
                >
                  Quote
                  <i className="fa-solid fa-chevron-down nav-chevron"></i>
                </Link>

                <div
                  className={`mega-menu-container${
                    isQuoteMenuOpen ? " mega-menu-is-open" : ""
                  }`}
                  onMouseEnter={handleQuoteMenuMouseEnter}
                  onMouseLeave={handleQuoteMenuMouseLeave}
                  role="region"
                  aria-label="Get a Quote menu"
                >
                  <div className="mega-menu-inner">

                    {/* ── Column 1 · Personal Quotes ── */}
                    <div className="mega-menu-col">
                      <div className="mega-menu-col-header">
                        <span className="mega-menu-col-header-icon">
                          <i className="fa-solid fa-user"></i>
                        </span>
                        <span className="mega-menu-col-title">
                          Personal
                        </span>
                      </div>

                      <Link
                        className="mega-menu-link"
                        to="/auto-quote-form"
                        onClick={handleQuoteMenuLinkClick}
                      >
                        <span className="mega-menu-link-icon-wrap">
                          <i className="fa-solid fa-car"></i>
                        </span>
                        <span className="mega-menu-link-body">
                          <span className="mega-menu-link-title">
                            Auto Insurance
                          </span>
                          <span className="mega-menu-link-desc">
                            Get an instant online auto quote and compare
                            the best rates available
                          </span>
                        </span>
                      </Link>

                      <Link
                        className="mega-menu-link"
                        to="/renters-insurance"
                        onClick={handleQuoteMenuLinkClick}
                      >
                        <span className="mega-menu-link-icon-wrap">
                          <i className="fa-solid fa-couch"></i>
                        </span>
                        <span className="mega-menu-link-body">
                          <span className="mega-menu-link-title">
                            Renters Insurance
                          </span>
                          <span className="mega-menu-link-desc">
                            Affordable quotes for your apartment or
                            rental home — starting in minutes
                          </span>
                        </span>
                      </Link>

                      <HashLink
                        smooth
                        scroll={scrollWithOffset}
                        className="mega-menu-link"
                        to="/our-products#home-insurance"
                        onClick={handleQuoteMenuLinkClick}
                      >
                        <span className="mega-menu-link-icon-wrap">
                          <i className="fa-solid fa-house"></i>
                        </span>
                        <span className="mega-menu-link-body">
                          <span className="mega-menu-link-title">
                            Home Insurance
                          </span>
                          <span className="mega-menu-link-desc">
                            Protect your home &amp; belongings — get a
                            free estimate today
                          </span>
                        </span>
                      </HashLink>

                      <Link
                        className="mega-menu-link"
                        to="/life-insurance"
                        onClick={handleQuoteMenuLinkClick}
                      >
                        <span className="mega-menu-link-icon-wrap">
                          <i className="fa-solid fa-heart"></i>
                        </span>
                        <span className="mega-menu-link-body">
                          <span className="mega-menu-link-title">
                            Life Insurance
                          </span>
                          <span className="mega-menu-link-desc">
                            Explore life coverage options through
                            Progressive — protect your family's future
                          </span>
                        </span>
                      </Link>

                      <Link
                        className="mega-menu-view-all"
                        to="/get-quote-today"
                        onClick={handleQuoteMenuLinkClick}
                      >
                        All Quote Types&nbsp;
                        <i className="fa-solid fa-arrow-right"></i>
                      </Link>
                    </div>

                    {/* ── Column 2 · Business Quotes ── */}
                    <div className="mega-menu-col">
                      <div className="mega-menu-col-header">
                        <span className="mega-menu-col-header-icon">
                          <i className="fa-solid fa-briefcase"></i>
                        </span>
                        <span className="mega-menu-col-title">
                          Business
                        </span>
                      </div>

                      <HashLink
                        smooth
                        scroll={scrollWithOffset}
                        className="mega-menu-link"
                        to="/our-products#commercial-insurance"
                        onClick={handleQuoteMenuLinkClick}
                      >
                        <span className="mega-menu-link-icon-wrap">
                          <i className="fa-solid fa-building"></i>
                        </span>
                        <span className="mega-menu-link-body">
                          <span className="mega-menu-link-title">
                            Commercial Insurance
                          </span>
                          <span className="mega-menu-link-desc">
                            Tailored business coverage quotes for
                            companies of every size
                          </span>
                        </span>
                      </HashLink>

                      <Link
                        className="mega-menu-link"
                        to="/get-general-liability"
                        onClick={handleQuoteMenuLinkClick}
                      >
                        <span className="mega-menu-link-icon-wrap">
                          <i className="fa-solid fa-shield-halved"></i>
                        </span>
                        <span className="mega-menu-link-body">
                          <span className="mega-menu-link-title">
                            Errors &amp; Omissions / E&amp;O
                          </span>
                          <span className="mega-menu-link-desc">
                            Quote E&amp;O coverage to protect your
                            business from third-party claims
                          </span>
                        </span>
                      </Link>

                      <Link
                        className="mega-menu-link"
                        to="/get-business-liability-quote"
                        onClick={handleQuoteMenuLinkClick}
                      >
                        <span className="mega-menu-link-icon-wrap">
                          <i className="fa-solid fa-building-shield"></i>
                        </span>
                        <span className="mega-menu-link-body">
                          <span className="mega-menu-link-title">
                            General Business Liability
                          </span>
                          <span className="mega-menu-link-desc">
                            Request business liability coverage for
                            your operations, employees &amp; locations
                          </span>
                        </span>
                      </Link>

                      <HashLink
                        smooth
                        scroll={scrollWithOffset}
                        className="mega-menu-link"
                        to="/our-products#commercial-insurance"
                        onClick={handleQuoteMenuLinkClick}
                      >
                        <span className="mega-menu-link-icon-wrap">
                          <i className="fa-solid fa-warehouse"></i>
                        </span>
                        <span className="mega-menu-link-body">
                          <span className="mega-menu-link-title">
                            Property Insurance
                          </span>
                          <span className="mega-menu-link-desc">
                            Protect your business assets, equipment
                            &amp; inventory
                          </span>
                        </span>
                      </HashLink>
                    </div>

                    {/* ── Column 3 · CTA Card ── */}
                    <div className="mega-menu-cta-col">
                      <div className="mega-menu-cta-card mega-menu-cta-card--quote">
                        <span className="mega-menu-cta-badge">
                          <i className="fa-solid fa-bolt"></i>&nbsp;Fast
                          &amp; Free
                        </span>
                        <p className="mega-menu-cta-title">
                          Get your free quote in minutes
                        </p>
                        <p className="mega-menu-cta-text">
                          Compare rates from top carriers and find the
                          right plan for your needs.
                        </p>
                        <Link
                          className="mega-menu-cta-btn"
                          to="/get-quote-today"
                          onClick={handleQuoteMenuLinkClick}
                        >
                          <i className="fa-solid fa-file-invoice"></i>&nbsp;See
                          All Quote Types
                        </Link>
                        <Link
                          className="mega-menu-cta-secondary"
                          to="/contact-us"
                          onClick={handleQuoteMenuLinkClick}
                        >
                          <i className="fa-regular fa-envelope"></i>&nbsp;Speak
                          to an Agent
                        </Link>
                      </div>
                    </div>

                  </div>
                </div>
              </li>
              {/* ── End Quote · Mega Menu ──────────────────────────── */}

              <li className="nav-item nav-dropdown">
                <Link
                  className="nav-link nav-dropdown-toggle nav-text"
                  to="#"
                  role="button"
                  aria-expanded="false"
                >
                  About Us
                  <i className="fa-solid fa-chevron-down nav-chevron"></i>
                </Link>
                <ul className="nav-dropdown-menu list-unstyled">
                  <li>
                    <HashLink
                      smooth
                      scroll={scrollWithOffset}
                      className="nav-dropdown-item"
                      to="/about-us#about-top"
                    >
                      <i className="fa-solid fa-info-circle nav-icon"></i>
                      <span>About Kaphy Insurance Agency</span>
                    </HashLink>
                  </li>
                  <li>
                    <HashLink
                      smooth
                      scroll={scrollWithOffset}
                      className="nav-dropdown-item"
                      to="/about-us#our-story"
                    >
                      <i className="fa-solid fa-book-open nav-icon"></i>
                      <span>Our Story</span>
                    </HashLink>
                  </li>
                  <li>
                    <HashLink
                      smooth
                      scroll={scrollWithOffset}
                      className="nav-dropdown-item"
                      to="/about-us#our-mission"
                    >
                      <i className="fa-solid fa-bullseye nav-icon"></i>
                      <span>Our Mission</span>
                    </HashLink>
                  </li>
                </ul>
              </li>

              <li className="nav-item nav-dropdown">
                <Link
                  className="nav-link nav-dropdown-toggle nav-text"
                  to="#"
                  role="button"
                  aria-expanded="false"
                >
                  Our Office
                  <i className="fa-solid fa-chevron-down nav-chevron"></i>
                </Link>
                <ul className="nav-dropdown-menu list-unstyled">
                  <li>
                    <Link
                      className="nav-dropdown-item"
                      to="/location/our-locations"
                    >
                      <i className="fa-solid fa-location-dot nav-icon"></i>
                      <span>Our Locations</span>
                    </Link>
                  </li>
                  <li>
                    <Link className="nav-dropdown-item" to="/office-open-hours">
                      <i className="fa-regular fa-clock nav-icon"></i>
                      <span>Open Hours</span>
                    </Link>
                  </li>
                </ul>
              </li>

              {/* ── Service Areas · Locations Dropdown ──────────────────── */}
              <li
                className="nav-item nav-dropdown nav-mega-item"
                ref={locationsMenuRef}
                onMouseEnter={handleLocationsMenuMouseEnter}
                onMouseLeave={handleLocationsMenuMouseLeave}
              >
                <Link
                  className="nav-link nav-dropdown-toggle mega-menu-toggle nav-text"
                  to="#"
                  role="button"
                  aria-haspopup="true"
                  aria-expanded={isLocationsMenuOpen}
                  onClick={handleLocationsMenuToggle}
                >
                  Service Areas
                  <i className="fa-solid fa-chevron-down nav-chevron"></i>
                </Link>

                <div
                  className={`nav-locations-container${
                    isLocationsMenuOpen ? " nav-locations-is-open" : ""
                  }`}
                  onMouseEnter={handleLocationsMenuMouseEnter}
                  onMouseLeave={handleLocationsMenuMouseLeave}
                  role="region"
                  aria-label="Service Areas menu"
                >
                  <div className="nav-locations-header">
                    <i className="fa-solid fa-map-location-dot nav-locations-header-icon"></i>
                    <span>Minnesota Service Areas</span>
                  </div>
                  <div className="nav-locations-grid">
                    <Link className="nav-locations-item" to="/minneapolis-insurance-agency" onClick={handleLocationsMenuLinkClick}>
                      <i className="fa-solid fa-location-dot nav-icon"></i>
                      <span>Minneapolis</span>
                    </Link>
                    <Link className="nav-locations-item" to="/insurance/brooklyn-park-mn" onClick={handleLocationsMenuLinkClick}>
                      <i className="fa-solid fa-location-dot nav-icon"></i>
                      <span>Brooklyn Park</span>
                    </Link>
                    <Link className="nav-locations-item" to="/insurance/maple-grove-mn" onClick={handleLocationsMenuLinkClick}>
                      <i className="fa-solid fa-location-dot nav-icon"></i>
                      <span>Maple Grove</span>
                    </Link>
                    <Link className="nav-locations-item" to="/insurance/plymouth-mn" onClick={handleLocationsMenuLinkClick}>
                      <i className="fa-solid fa-location-dot nav-icon"></i>
                      <span>Plymouth</span>
                    </Link>
                    <Link className="nav-locations-item" to="/insurance/bloomington-mn" onClick={handleLocationsMenuLinkClick}>
                      <i className="fa-solid fa-location-dot nav-icon"></i>
                      <span>Bloomington</span>
                    </Link>
                    <Link className="nav-locations-item" to="/insurance/mankato-mn" onClick={handleLocationsMenuLinkClick}>
                      <i className="fa-solid fa-location-dot nav-icon"></i>
                      <span>Mankato</span>
                    </Link>
                    <Link className="nav-locations-item" to="/insurance/burnsville-mn" onClick={handleLocationsMenuLinkClick}>
                      <i className="fa-solid fa-location-dot nav-icon"></i>
                      <span>Burnsville</span>
                    </Link>
                    <Link className="nav-locations-item" to="/insurance/rochester-mn" onClick={handleLocationsMenuLinkClick}>
                      <i className="fa-solid fa-location-dot nav-icon"></i>
                      <span>Rochester</span>
                    </Link>
                    <Link className="nav-locations-item" to="/insurance/saint-cloud-mn" onClick={handleLocationsMenuLinkClick}>
                      <i className="fa-solid fa-location-dot nav-icon"></i>
                      <span>Saint Cloud</span>
                    </Link>
                    <Link className="nav-locations-item" to="/insurance/sartell-mn" onClick={handleLocationsMenuLinkClick}>
                      <i className="fa-solid fa-location-dot nav-icon"></i>
                      <span>Sartell</span>
                    </Link>
                    <Link className="nav-locations-item" to="/insurance/sauk-rapids-mn" onClick={handleLocationsMenuLinkClick}>
                      <i className="fa-solid fa-location-dot nav-icon"></i>
                      <span>Sauk Rapids</span>
                    </Link>
                    <Link className="nav-locations-item" to="/insurance/waite-park-mn" onClick={handleLocationsMenuLinkClick}>
                      <i className="fa-solid fa-location-dot nav-icon"></i>
                      <span>Waite Park</span>
                    </Link>
                    <Link className="nav-locations-item" to="/insurance/st-joseph-mn" onClick={handleLocationsMenuLinkClick}>
                      <i className="fa-solid fa-location-dot nav-icon"></i>
                      <span>St. Joseph</span>
                    </Link>
                  </div>
                  <div className="nav-locations-footer">
                    <Link className="nav-locations-all-link" to="/location/our-locations" onClick={handleLocationsMenuLinkClick}>
                      <i className="fa-solid fa-map"></i> View All Locations
                      <i className="fa-solid fa-arrow-right"></i>
                    </Link>
                  </div>
                </div>
              </li>
              {/* ── End Service Areas · Locations Dropdown ──────────────── */}

              <li className="nav-item">
                <Link className="nav-link nav-text" to="/contact-us">
                  Contact Us
                </Link>
              </li>

              <li className="nav-item">
                <Link
                  className="nav-link nav-cta-btn nav-appointment"
                  to="/appointments"
                >
                  <i className="fa-solid fa-calendar-check nav-btn-icon"></i>
                  <span>Book Appointment</span>
                </Link>
              </li>

              {user ? (
                <li className="nav-item">
                  <button
                    className="nav-link nav-cta-btn nav-logout"
                    onClick={handleLogout}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    <i className="fa-solid fa-right-from-bracket nav-btn-icon"></i>
                    <span>Logout</span>
                  </button>
                </li>
              ) : (
                <li className="nav-item">
                  <Link
                    className="nav-link nav-cta-btn nav-login"
                    to="/api/auth/login"
                  >
                    <i className="fa-solid fa-right-to-bracket text-light nav-btn-icon"></i>
                    <span className="text-light">Login</span>
                  </Link>
                </li>
              )}
            </ul>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navigation;
